import {isEqual, isUndefined, mapValues, omit, union, without} from 'lodash';
import {Mutable} from 'utility-types';

import {
  buildConversationMessagePartial,
  findContentUpdatedAt,
  mergeLastComment,
  mergeLastManualMessage,
  mergeLastMessage,
} from '../../../helpers/models/conversationHelpers';
import {isDraftMessage, isServerMessage} from '../../../helpers/models/messageHelpers';
import {isShallowEqual} from '../../../helpers/reselectHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {CommentModel, CommentStatusesEnum} from '../../../models/api/commentModel';
import {ConversationModel} from '../../../models/api/conversationModel';
import {MessageModel, MessageVisualStatusesEnum} from '../../../models/api/messageModel';
import {AppAction} from '../../appActions';
import {deleteFromResourceTable, ResourceMap, ResourceTable} from '../../appTypes';
import {DataGroupsActionsEnum, DataUpdatedAction} from '../dataGroupsActions';
import {TagsActionsEnum} from '../tags/tagsActions';
import {ConversationsActionsEnum, SafetyNetOptimisticallySatisfiedAction} from './conversationsActions';

/*
 * Reducer.
 */

export function conversationsReducer(
  userId: number,
  state: ResourceTable<number, ConversationModel>,
  action: AppAction,
): ResourceTable<number, ConversationModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED: {
      const {byId, deletedIds} = state;
      const newById = conversationsUpdatedReducer(userId, byId, action, (id) => deletedIds.contains(id));

      if (byId === newById) {
        return state;
      }

      return {
        ...state,
        byId: newById,
      };
    }

    case ConversationsActionsEnum.CONVERSATION_CONTENT_UPDATED: {
      const {conversationId, contentUpdatedAt} = action.payload;
      const conversation = state.byId[conversationId];
      if (!conversation) {
        return state;
      }

      // TODO: Should we use mergeConversationContent, to reduce duplication and to update lastComment/lastMessage?
      const existingContentUpdatedAt = findContentUpdatedAt(conversation.contentUpdatedDates, userId);
      if (existingContentUpdatedAt > contentUpdatedAt) {
        return state;
      }

      return {
        ...state,
        byId: {
          ...state.byId,
          [conversationId]: {
            ...conversation,
            isContentLoaded: true,
            draftCommentUids: action.payload.draftCommentUids,
            draftMessageUids: action.payload.draftMessageUids,
          },
        },
      };
    }

    case ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED:
      // Don't keep a tombstone in case we regain access to the conversation.
      return {
        ...state,
        byId: omit(state.byId, action.payload.conversationId),
      };

    case ConversationsActionsEnum.CONVERSATION_DELETED:
      return deleteFromResourceTable(state, action.payload.conversationId);

    case TagsActionsEnum.TAG_DELETION_CONFIRMED:
      // If the conversation includes the tag being removed, remove it.
      // Otherwise, just return the conversation.
      return {
        ...state,
        byId: mapValues(state.byId, (c) =>
          c && c.tagIds.includes(action.payload)
            ? {
                ...c,
                tagIds: without(c.tagIds, action.payload),
              }
            : c,
        ),
      };

    case ConversationsActionsEnum.SAFETY_NET_OPTIMISTICALLY_SATISFIED:
      return safetyNetReducer(state, action);

    default:
      return state;
  }
}

/*
 * Constants.
 */

// TODO: Decide if we should add draftSeeds, since it is timeline/activity-related.
const conversationContentKeys = [
  // TODO: Decide if we can remove this and the corresponding test case, since
  // timelineUpdatedAt is not bumped when the subject is changed.
  'subject',
  'isContentLoaded',
  'draftCommentUids',
  'draftMessageUids',
  'lastComment',
  'lastMessage',
  'lastManualMessage',
  'contentUpdatedDates',
] as const;

const conversationSeenByKeys = ['seenAt', 'autoSeenAt', 'seenUpdatedAt'] as const;

/*
 * Types.
 */

type ConversationContent = Required<Pick<ConversationModel, typeof conversationContentKeys[any]>>;
type ConversationSeenBy = Required<Pick<ConversationModel, typeof conversationSeenByKeys[any]>>;

/*
 * Helpers.
 */

function conversationsUpdatedReducer(
  userId: number,
  state: ResourceMap<ConversationModel>,
  action: DataUpdatedAction,
  isDeletedId: (id: number) => boolean,
): ResourceMap<ConversationModel> {
  const {shouldIgnoreUpdatedAt} = action.payload;

  // Either the new state, or undefined if there are no state changes.
  let newState: Mutable<ResourceMap<ConversationModel>> | undefined;

  const conversationDuplicates = action.payload.result.conversationDuplicates || [];
  conversationDuplicates.forEach((duplicate) => {
    const existingConversation = (newState || state)[duplicate.conversationId];

    if (!existingConversation) {
      return;
    }

    const updatedConversation = {
      ...existingConversation,
      duplicateConversationIds: duplicate.duplicateIds,
      shouldShowDuplicateBanner: duplicate.shouldShowDuplicateBanner,
    };

    newState = newState || {...state};
    newState[updatedConversation.id] = updatedConversation;
  });

  // Optimization: iterate the updates once instead of _().groupBy/keyBy/sortBy/maxBy.
  const conversations = action.payload.result.conversations || [];
  conversations.forEach((updatedConversation) => {
    if (isDeletedId(updatedConversation.id)) {
      return;
    }

    const existingConversation = (newState || state)[updatedConversation.id];

    // There is no existing conversation, return early.
    if (!existingConversation) {
      // Optimization: create a copy of the state once on the first state change.
      newState = newState || {...state};
      newState[updatedConversation.id] = updatedConversation;
      return;
    }

    // Merge the different parts of the conversation.
    const mergedConversation = mergeConversation(
      existingConversation,
      updatedConversation,
      shouldIgnoreUpdatedAt,
    );
    const mergedContent = mergeConversationContent(
      userId,
      existingConversation,
      updatedConversation,
      shouldIgnoreUpdatedAt,
    );
    const mergedSeenBy = mergeConversationSeenBy(
      existingConversation,
      updatedConversation,
      shouldIgnoreUpdatedAt,
    );

    // Re-combine the merged parts.
    const newConversation = {...mergedConversation};
    // Optimization: simple assign in loop, instead of {..._.pick}.
    conversationContentKeys.forEach((key) => {
      // TODO: Convince TypeScript this is safe... but this might be impossible.
      // @ts-expect-error
      newConversation[key] = mergedContent[key];
    });
    conversationSeenByKeys.forEach((key) => {
      newConversation[key] = mergedSeenBy[key];
    });

    // Use a shallow comparison to avoid unnecessary state changes.
    if (isShallowEqual(newConversation, existingConversation)) {
      return;
    }

    // Optimization: create a copy of the state once on the first state change.
    newState = newState || {...state};
    newState[updatedConversation.id] = newConversation;
  });

  // Iterate the comment updates also.
  const comments = action.payload.result.comments || [];
  comments.forEach((comment) => {
    const conversation = (newState || state)[comment.conversationId];
    if (!conversation) {
      return;
    }

    const newConversation = mergeComment(conversation, comment);
    if (newConversation === conversation) {
      return;
    }

    newState = newState || {...state};
    newState[conversation.id] = newConversation;
  });

  const activities = action.payload.result.activities || [];

  // Iterate the message updates also.
  const messages = action.payload.result.messages || [];
  messages.forEach((message) => {
    const conversation = (newState || state)[message.conversationId];
    if (!conversation) {
      return;
    }

    const messageActivity = activities.find(
      (activity) =>
        activity.visualType === ActivityVisualTypesEnum.MESSAGE && activity.messageId === message.id,
    );
    const newConversation = mergeMessage(conversation, message, messageActivity);
    if (newConversation === conversation) {
      return;
    }

    newState = newState || {...state};
    newState[conversation.id] = newConversation;
  });

  return newState || state;
}

function mergeConversation(
  existing: ConversationModel,
  updated: ConversationModel,
  shouldIgnoreUpdatedAt: boolean,
): ConversationModel {
  // The existing conversation is strictly newer.
  if (!shouldIgnoreUpdatedAt && existing.updatedAt > updated.updatedAt) {
    return existing;
  }

  return {
    ...updated,
    // Merge client-only properties.
    safetyNets: mergeSafetyNets(existing.safetyNets, updated.safetyNets),
    unsavedDraftCommentUids: updated.unsavedDraftCommentUids || existing.unsavedDraftCommentUids,
    unsavedDraftMessageUids: updated.unsavedDraftMessageUids || existing.unsavedDraftMessageUids,
    offlineSeenId: updated.offlineSeenId || existing.offlineSeenId,
    // These properties are handled separately in conversationsUpdatedReducer.
    duplicateConversationIds: existing.duplicateConversationIds,
    shouldShowDuplicateBanner: existing.shouldShowDuplicateBanner,
  };
}

type ConversationSafetyNets = ConversationModel['safetyNets'];

function mergeSafetyNets(
  existing: ConversationSafetyNets,
  updated: ConversationSafetyNets,
): ConversationSafetyNets {
  if (!existing || !updated) {
    return updated;
  }

  return updated.map((updatedSafetyNet) => {
    const isOptimisticallySatisfied = existing.find(
      (existingSafetyNet) =>
        existingSafetyNet.id === updatedSafetyNet.id &&
        existingSafetyNet.updatedAt >= updatedSafetyNet.updatedAt,
    )?.isOptimisticallySatisfied;

    if (isOptimisticallySatisfied === undefined) {
      return updatedSafetyNet;
    }

    return {
      ...updatedSafetyNet,
      isOptimisticallySatisfied,
    };
  });
}

function mergeConversationContent(
  userId: number,
  existing: ConversationModel,
  updated: ConversationModel,
  shouldIgnoreUpdatedAt: boolean,
): ConversationContent {
  // The existing conversation has strictly newer content.
  const existingContentUpdatedAt = findContentUpdatedAt(existing.contentUpdatedDates, userId);
  const updatedContentUpdatedAt = findContentUpdatedAt(updated.contentUpdatedDates, userId);
  if (!shouldIgnoreUpdatedAt && existingContentUpdatedAt > updatedContentUpdatedAt) {
    return existing;
  }

  return {
    subject: updated.subject,
    contentUpdatedDates: updated.contentUpdatedDates,
    isContentLoaded: updated.isContentLoaded || existing.isContentLoaded,
    draftCommentUids: updated.isContentLoaded
      ? updated.draftCommentUids
      : mergeConversationDraftUids(existing.draftCommentUids, updated.draftCommentUids),
    draftMessageUids: updated.isContentLoaded
      ? updated.draftMessageUids
      : mergeConversationDraftUids(existing.draftMessageUids, updated.draftMessageUids),
    lastComment: mergeLastComment(existing.lastComment, updated.lastComment),
    // It is safe to use mergeLastMessage for lastManualMessage here because updated.lastManualMessage is guaranteed to not be an auto-response.
    lastManualMessage: mergeLastMessage(existing.lastManualMessage, updated.lastManualMessage),
    lastMessage: mergeLastMessage(existing.lastMessage, updated.lastMessage),
  };
}

function mergeConversationDraftUids(
  existing: ReadonlyArray<string>,
  updated: ReadonlyArray<string>,
): ReadonlyArray<string> {
  const merged = union(existing, updated);

  // Prefer to return the existing array instance.
  return isEqual(merged, existing) ? existing : merged;
}

function mergeConversationSeenBy(
  existing: ConversationModel,
  updated: ConversationModel,
  shouldIgnoreUpdatedAt: boolean,
): ConversationSeenBy {
  // The updated conversation is missing "seen by" data.
  if (isUndefined(updated.seenAt) && isUndefined(updated.autoSeenAt)) {
    return existing;
  }

  // The existing conversation has strictly newer "seen by" data.
  if (
    !shouldIgnoreUpdatedAt &&
    existing.seenUpdatedAt &&
    updated.seenUpdatedAt &&
    updated.seenUpdatedAt < existing.seenUpdatedAt
  ) {
    return existing;
  }

  // Use the updated conversation's "seen by" data.
  return updated;
}

function mergeComment(conversation: ConversationModel, comment: CommentModel): ConversationModel {
  let newConversation = conversation;

  // If this comment is not part of the drafts, we may need to add it.
  const {draftCommentUids} = newConversation;
  if (comment.status !== CommentStatusesEnum.SENT && !draftCommentUids.includes(comment.uid)) {
    newConversation = {
      ...newConversation,
      draftCommentUids: [...draftCommentUids, comment.uid],
    };
  }
  // If this comment is part of the drafts, we may need to remove it.
  else if (comment.status === CommentStatusesEnum.SENT && draftCommentUids.includes(comment.uid)) {
    newConversation = {
      ...newConversation,
      draftCommentUids: without(draftCommentUids, comment.uid),
    };
  }

  // This comment may be the new last comment.
  newConversation = maybeUpdateLastComment(newConversation, comment);

  return newConversation;
}

function maybeUpdateLastComment(conversation: ConversationModel, comment: CommentModel): ConversationModel {
  if (comment.status !== CommentStatusesEnum.SENT) {
    return conversation;
  }

  // Does the conversation already have a later comment?
  const {lastComment} = conversation;
  if (lastComment && lastComment.sentAt > comment.sentAt) {
    return conversation;
  }

  const {uid, sentAt} = comment;
  return {
    ...conversation,
    lastComment: mergeLastComment(lastComment, {uid, sentAt}),
  };
}

function mergeMessage(
  conversation: ConversationModel,
  message: MessageModel,
  messageActivity: ActivityModel | undefined,
): ConversationModel {
  let newConversation = conversation;

  // If this message is not part of the drafts, we may need to add it.
  const {draftMessageUids} = newConversation;
  const wasSaved = isServerMessage(message);

  if (isDraftMessage(message) && wasSaved && !draftMessageUids.includes(message.uid)) {
    newConversation = {
      ...newConversation,
      draftMessageUids: [...draftMessageUids, message.uid],
    };
  }
  // If this message is part of the drafts, we may need to remove it.
  else if (!isDraftMessage(message) && draftMessageUids.includes(message.uid)) {
    newConversation = {
      ...newConversation,
      draftMessageUids: without(draftMessageUids, message.uid),
    };
  }

  // If this message was saved, remove it from the unsaved drafts.
  const {unsavedDraftMessageUids = []} = newConversation;
  if (wasSaved && unsavedDraftMessageUids.includes(message.uid)) {
    newConversation = {
      ...newConversation,
      unsavedDraftMessageUids: without(unsavedDraftMessageUids, message.uid),
    };
  }
  // If this message was not saved, add it to the unsaved drafts.
  else if (!wasSaved && !unsavedDraftMessageUids.includes(message.uid)) {
    newConversation = {
      ...newConversation,
      unsavedDraftMessageUids: [...unsavedDraftMessageUids, message.uid],
    };
  }

  // This message may be the new last message.
  newConversation = maybeUpdateLastMessageData(newConversation, message, messageActivity);

  return newConversation;
}

// Will set the new message as lastMessage and/or lastManualMessage if appropriate.
function maybeUpdateLastMessageData(
  conversation: ConversationModel,
  newMessage: MessageModel,
  messageActivity: ActivityModel | undefined,
): ConversationModel {
  if (newMessage.visualStatus !== MessageVisualStatusesEnum.TIMELINE) {
    return conversation;
  }

  const {lastMessage, lastManualMessage} = conversation;

  const newMessagePartial = buildConversationMessagePartial(newMessage);
  return {
    ...conversation,
    lastMessage: mergeLastMessage(lastMessage, newMessagePartial),
    // We must use mergeLastManualMessage (instead of the simpler mergeLastMessage) because we don't know if newMessage is an auto-response.
    lastManualMessage: mergeLastManualMessage(lastManualMessage, newMessagePartial, messageActivity),
  };
}

function safetyNetReducer(
  state: ResourceTable<number, ConversationModel>,
  action: SafetyNetOptimisticallySatisfiedAction,
): ResourceTable<number, ConversationModel> {
  const {conversationId, safetyNetId, isOptimisticallySatisfied} = action.payload;
  const conversation = state.byId[conversationId];

  if (!conversation) {
    return state;
  }

  const updatedConversation = {
    ...conversation,
    safetyNets: conversation.safetyNets?.map((safetyNet) => {
      if (safetyNet.id !== safetyNetId) {
        return safetyNet;
      }

      return {
        ...safetyNet,
        isOptimisticallySatisfied,
      };
    }),
  };

  return {
    ...state,
    byId: {
      ...state.byId,
      [conversationId]: updatedConversation,
    },
  };
}
